// src/routesConfig.tsx
import React from "react";
import { ReactElement, lazy } from "react";
import Invitation from "./pages/Invitation";
import BeginTest from "./pages/BeginTest";
import TestComplete from "./pages/TestComplete";
import TestDashboard from "./pages/TestDashboard";
import CandidateDashboard from "./pages/CandidateDashboard";
import BrowseTests from "./pages/BrowseTests";
import BusinessAssessment from "./pages/BusinessAssessment";
import ViewCandidates from "./pages/ViewCandidates";
import TestDetails from "./pages/TestDetails";
import IndustriesList from "./pages/IndustriesList";
import ViewIndustry from "./pages/ViewIndustry";
import TestBuilder from "./pages/CreateOrEditTest";
import CandidateTestResult from "./pages/Candidate/CandidateTestResult";
import IndustryFunctions from "./pages/IndustryFunctions";
import IndustryRoles from "./pages/IndustryRoles";
import IndustrySoftware from "./pages/IndustrySoftware";
import TalentPool from "./pages/TalentPool";
import TalentPoolFilterScreen from "./pages/TalentPoolFilterScreen";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancelled from "./pages/SubscriptionCancelled";

const Register = lazy(() => import("./pages/Register"));
const RegisterBusiness = lazy(() => import("./pages/RegisterBusiness"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Assessment = lazy(() => import("./pages/Assessment"));
const Help = lazy(() => import("./pages/Help"));
const RegisterCandidate = lazy(
  () => import("./pages/Candidate/RegisterCandidate")
);
const CompareCandidate = lazy(
  () => import("./pages/Candidate/CompareCandidate")
);
const TrackProgress = lazy(() => import("./pages/Candidate/TrackProgress"));
const Question = lazy(() => import("./pages/Candidate/Question"));
const TestResult = lazy(() => import("./pages/Test/TestResult"));
const Profile = lazy(() => import("./pages/User/Profile"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const Enquiry = lazy(() => import("./pages/User/Enquiry"));
const PersonalProPlan = lazy(() => import("./pages/Candidate/PersonalProPlan"));
const Checkout = lazy(() => import("./pages/User/Checkout"));
const UserSettings = lazy(() => import("./pages/User/UserSettings"));
const ApiKeySettings = lazy(() => import("./pages/User/ApiKeySettings"));
const ApiSubscribe = lazy(() => import("./pages/User/ApiSubscribe"));
const B2BSessions = lazy(() => import("./pages/Admin/B2BSessions"));
const B2BApiClients = lazy(() => import("./pages/Admin/B2BApiClients"));
const B2BApiSubscriptions = lazy(() => import("./pages/Admin/B2BApiSubscriptions"));
const ApiIntegrationGuide = lazy(() => import("./pages/Docs/ApiIntegrationGuide"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const VerifyPage = lazy(() => import("./pages/VerifyPage"));
const ExtAuth = lazy(() => import("./pages/Ext/ExtAuth"));
const ExtError = lazy(() => import("./pages/Ext/ExtError"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const CandidateDetails = lazy(() => import("./pages/CandidateDetails"));

export interface AppRoute {
  path: string;
  element: ReactElement;
  auth?: boolean;
  sidebar?: boolean;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireSubscription?: boolean;
  // B2B external API users (is_external_api_user=true) are restricted to routes
  // marked with this flag. RequiredAuth redirects them away from anything else.
  externalUserAllowed?: boolean;
}

const routes: AppRoute[] = [
  // B2B external session pages (no auth, no sidebar)
  { path: "/ext/auth", element: <ExtAuth />, auth: false, sidebar: false },
  { path: "/ext/error", element: <ExtError />, auth: false, sidebar: false },

  // public, no sidebar
  { path: "/register", element: <Register />, auth: false, sidebar: false },
  { path: "/register-business", element: <RegisterBusiness />, auth: false, sidebar: false },
  { path: "/login", element: <Login />, auth: false, sidebar: false },
  { path: "/forgot-password", element: <ForgotPassword />, auth: false, sidebar: false },
  { path: "/reset-password", element: <ResetPassword />, auth: false, sidebar: false },
  { path: "/invitation", element: <Invitation />, auth: false, sidebar: false },

  // Stripe subscription feedback pages
  { path: "/subscription/success", element: <SubscriptionSuccess />, auth: false, sidebar: false },
  { path: "/subscription/cancelled", element: <SubscriptionCancelled />, auth: false, sidebar: false },

  //Candidate workflow
  { path: "/begin-test", element: <BeginTest />, auth: true, sidebar: true, requireSubscription: true, externalUserAllowed: true },
  {
    path: "/candidate/question",
    element: <Question />,
    auth: true,
    sidebar: false,
    requireSubscription: true,
    externalUserAllowed: true,
  },
  {
    path: "/test-complete",
    element: <TestComplete />,
    auth: false,
    sidebar: false,
    externalUserAllowed: true,
  },
  {
    path: "/test-dashboard",
    // element: <TestDashboard />,
    element: <CandidateDashboard />,
    auth: true,
    sidebar: true,
    requireSubscription: true,
  },
  // {
  //   path: "/candidate-dashboard",
  //   element: <CandidateDashboard />,
  //   auth: true,
  //   sidebar: false,
  // },
  {
    path: "/browse-tests",
    element: <BrowseTests />,
    auth: true,
    sidebar: true,
    requireSubscription: true,
  },
  {
    path: "/test-details",
    element: <TestDetails />,
    auth: true,
    sidebar: true,
    requireSubscription: true,
  },
  { path: "/user/checkout", element: <Checkout />, auth: true, sidebar: false },
  {
    path: "/payment-success",
    element: <PaymentSuccess />,
    auth: true,
    sidebar: false,
  },
  {
    path: "/candidate/test-result",
    element: <CandidateTestResult />,
    auth: true,
    sidebar: true,
    requireSubscription: true,
  },
  { path: "/user/profile", element: <Profile />, auth: true, sidebar: true, requireSubscription: true },

  {
    path: "/user-settings",
    element: <UserSettings />,
    auth: true,
    sidebar: true,
  },
  {
    path: "/api-key-settings",
    element: <ApiKeySettings />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
  },
  {
    // Accept page for a super-admin-issued API subscription invitation.
    // Requires login (the invitation is bound to a specific business user
    // and the backend will 403 if the logged-in user doesn't match), but
    // no requireAdmin gate — a brand-new business that doesn't yet hold an
    // API key still needs to land here. Hidden from the sidebar; reachable
    // only via the email link.
    path: "/api-subscribe/:token",
    element: <ApiSubscribe />,
    auth: true,
    sidebar: false,
  },
  {
    // Public B2B API integration guide. Rendered from the markdown at
    // public/docs/api-integration.md (built copy of docs/b2b-api-
    // integration-guide.md). Linked from the welcome email; auth-free
    // so integrators can share the URL with colleagues who don't yet
    // hold a TestInvest account.
    path: "/docs/api-integration",
    element: <ApiIntegrationGuide />,
    auth: false,
    sidebar: false,
  },
  {
    // Public system status page. Polls GET /api/status/ on a 30-second
    // interval and renders each subsystem's state. Auth-free so
    // integrators can share the link.
    path: "/status",
    element: <StatusPage />,
    auth: false,
    sidebar: false,
  },
  {
    // Public credential verification page. Visitors paste a
    // TI-XXXX-XXXX credential ID and we confirm authenticity with
    // non-PII metadata (test name, level, kind, date). Auth-free.
    path: "/verify",
    element: <VerifyPage />,
    auth: false,
    sidebar: false,
  },
  {
    path: "/candidate/ppp",
    element: <PersonalProPlan />,
    auth: true,
    sidebar: true,
    requireSubscription: true,
  },
  {
    path: "/help",
    element: <Help />,
    auth: true,
    sidebar: true,
  },

  //Admin workflow
  {
    path: "/dashboard",
    element: <Dashboard />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },

  {
    path: "/industries-list",
    element: <IndustriesList />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/view-industry/:id",
    element: <ViewIndustry />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/view-industry",
    element: <ViewIndustry />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },

  {
    path: "/assessments/:id",
    element: <Assessment />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/assessments",
    element: <Assessment />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/business-assessment",
    element: <BusinessAssessment />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/view-candidates",
    element: <ViewCandidates />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/candidate/register",
    element: <RegisterCandidate />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/candidate/compare",
    element: <CompareCandidate />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/candidate/track-progress/:id",
    element: <TrackProgress />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/test/result",
    element: <TestResult />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  // { path: '/enquiry', element: <Enquiry />, auth: true, sidebar: true },
  {
    path: "/talent-pool/start",
    element: <TalentPoolFilterScreen />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/talent-pool",
    element: <TalentPool />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/candidate/profile/:userId",
    element: <CandidateDetails />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/test-builder/:industryId/:testId?",
    element: <TestBuilder />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/subscriptions",
    element: <SubscriptionPage />,
    auth: true,
    sidebar: true,
    requireAdmin: true,
  },

  //super admin
  {
    path: "/manage/b2b-sessions",
    element: <B2BSessions />,
    auth: true,
    sidebar: true,
    requireSuperAdmin: true,
  },
  {
    path: "/manage/api-clients",
    element: <B2BApiClients />,
    auth: true,
    sidebar: true,
    requireSuperAdmin: true,
  },
  {
    path: "/manage/api-subscriptions",
    element: <B2BApiSubscriptions />,
    auth: true,
    sidebar: true,
    requireSuperAdmin: true,
  },
  {
    path: "/industries/:industryId/functions",
    element: <IndustryFunctions />,
    auth: true,
    sidebar: true,
    requireSuperAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/industries/:industryId/roles",
    element: <IndustryRoles />,
    auth: true,
    sidebar: true,
    requireSuperAdmin: true,
    requireSubscription: true,
  },
  {
    path: "/industries/:industryId/software",
    element: <IndustrySoftware />,
    auth: true,
    sidebar: true,
    requireSuperAdmin: true,
    requireSubscription: true,
  },
];

export default routes;
